"use client";

import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useTranslation } from 'react-i18next';
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface FlowMapAppearance {
  showLegend?: boolean;
  legendLabel?: string;
  legendLocation?: "top-left" | "top-center" | "top-right" | "bottom-left" | "bottom-center" | "bottom-right";
  primaryCityColor?: string;
  secondaryCityColor?: string;
  lineColor?: string;
  showCityName?: boolean;
  primaryCityNameColor?: string;
  secondaryCityNameColor?: string;
  showValueTooltip?: boolean;
  showArrowHead?: boolean;
  countryFillColor?: string;
  countryHoverColor?: string;
  countryBorderColor?: string;
  showSubtitle?: boolean;
  subtitle?: string;
}

interface FlowMapAppearanceEditorProps {
  appearance: FlowMapAppearance;
  defaultLegendLabel?: string;
  onChange: (val: FlowMapAppearance) => void;
}

export default function FlowMapAppearanceEditor({ appearance, defaultLegendLabel = "Value", onChange }: FlowMapAppearanceEditorProps) {
  const { t } = useTranslation();

  const update = (u: Partial<FlowMapAppearance>) => onChange({ ...appearance, ...u });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('processManagement.mapAppearance')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Legend Settings */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Show Legend</Label>
            <Switch
              checked={!!appearance.showLegend}
              onCheckedChange={(v) => update({ showLegend: !!v })}
            />
          </div>
          {appearance.showLegend && (
            <>
              <div>
                <Label>Legend Label</Label>
                <Input
                  type="text"
                  value={appearance.legendLabel || defaultLegendLabel}
                  onChange={(e) => update({ legendLabel: e.target.value || defaultLegendLabel })}
                  placeholder={defaultLegendLabel}
                />
              </div>
              <div>
                <Label>Legend Location</Label>
                <Select
                  value={appearance.legendLocation || "bottom-center"}
                  onValueChange={(v: any) => update({ legendLocation: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top-left">Top Left</SelectItem>
                    <SelectItem value="top-center">Top Center</SelectItem>
                    <SelectItem value="top-right">Top Right</SelectItem>
                    <SelectItem value="bottom-left">Bottom Left</SelectItem>
                    <SelectItem value="bottom-center">Bottom Center</SelectItem>
                    <SelectItem value="bottom-right">Bottom Right</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        {/* City Marker Colors */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Primary City Color</Label>
            <Input
              type="color"
              value={appearance.primaryCityColor || "#ef4444"}
              onChange={(e) => update({ primaryCityColor: e.target.value })}
            />
          </div>
          <div>
            <Label>Secondary City Color</Label>
            <Input
              type="color"
              value={appearance.secondaryCityColor || "#06b6d4"}
              onChange={(e) => update({ secondaryCityColor: e.target.value })}
            />
          </div>
          <div>
            <Label>Line Color</Label>
            <Input
              type="color"
              value={appearance.lineColor || "#3b82f6"}
              onChange={(e) => update({ lineColor: e.target.value })}
            />
          </div>
        </div>

        {/* Country Colors */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Country Fill Color</Label>
            <Input
              type="color"
              value={appearance.countryFillColor || "#e5e7eb"}
              onChange={(e) => update({ countryFillColor: e.target.value })}
            />
          </div>
          <div>
            <Label>Country Hover Color</Label>
            <Input
              type="color"
              value={appearance.countryHoverColor || "#d1d5db"}
              onChange={(e) => update({ countryHoverColor: e.target.value })}
            />
          </div>
          <div>
            <Label>Country Border Color</Label>
            <Input
              type="color"
              value={appearance.countryBorderColor || "#d1d5db"}
              onChange={(e) => update({ countryBorderColor: e.target.value })}
            />
          </div>
        </div>

        {/* City Name Settings */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center justify-between">
            <Label>Show City Name</Label>
            <Switch
              checked={appearance.showCityName !== false}
              onCheckedChange={(v) => update({ showCityName: !!v })}
            />
          </div>
          {appearance.showCityName !== false && (
            <>
              <div>
                <Label>Primary City Name Color</Label>
                <Input
                  type="color"
                  value={appearance.primaryCityNameColor || "#ffffff"}
                  onChange={(e) => update({ primaryCityNameColor: e.target.value })}
                />
              </div>
              <div>
                <Label>Secondary City Name Color</Label>
                <Input
                  type="color"
                  value={appearance.secondaryCityNameColor || "#ffffff"}
                  onChange={(e) => update({ secondaryCityNameColor: e.target.value })}
                />
              </div>
            </>
          )}
        </div>

        {/* Tooltip Settings */}
        <div className="flex items-center justify-between">
          <Label>Show Value Tooltip on Hover</Label>
          <Switch
            checked={appearance.showValueTooltip !== false}
            onCheckedChange={(v) => update({ showValueTooltip: !!v })}
          />
        </div>

        {/* Arrow Head Settings */}
        <div className="flex items-center justify-between">
          <Label>Show Arrow Head on Lines</Label>
          <Switch
            checked={appearance.showArrowHead === true}
            onCheckedChange={(v) => update({ showArrowHead: !!v })}
          />
        </div>

        {/* Subtitle Settings */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Show Subtitle <span className="text-xs text-gray-500 font-normal">(Optional)</span></Label>
            <Switch
              checked={!!appearance.showSubtitle}
              onCheckedChange={(v) => update({ showSubtitle: !!v })}
            />
          </div>
          {appearance.showSubtitle && (
            <div>
              <Label>Subtitle</Label>
              <Input
                type="text"
                value={appearance.subtitle || ""}
                onChange={(e) => update({ subtitle: e.target.value })}
                placeholder="Enter subtitle"
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

